import type { OrgType } from './persona'
import type { FlywayRegion } from './properties'

export type AuthUser = {
  id: string
  email?: string
  fullName?: string
  avatarUrl?: string
}

export type OrgBasicInfo = {
  orgId: string
  orgType: OrgType
  name: string
}

export type PendingOrgInvitation = {
  id: string
  orgId: string
  orgType: OrgType
  orgName: string
  membershipId: string
  token: string
  role: string
}

export type OrgStatus = {
  hasActiveOwnerOrgs: boolean
  hasActiveOrgs: boolean
  hasCcEligibleOrgs: boolean
  hasPendingOwnerInvitations: boolean
  hasPendingMemberInvitations: boolean
  activeOrgs: OrgBasicInfo[]
  pendingOwnerInvitations: PendingOrgInvitation[]
  pendingMemberInvitations: PendingOrgInvitation[]
}

export const EMPTY_ORG_STATUS: OrgStatus = {
  hasActiveOwnerOrgs: false,
  hasActiveOrgs: false,
  hasCcEligibleOrgs: false,
  hasPendingOwnerInvitations: false,
  hasPendingMemberInvitations: false,
  activeOrgs: [],
  pendingOwnerInvitations: [],
  pendingMemberInvitations: [],
}

export function canUseCommandCenter(status: OrgStatus): boolean {
  return status.hasActiveOwnerOrgs || status.hasCcEligibleOrgs
}

export function hasNonManagerOrgAccess(status: OrgStatus): boolean {
  return status.hasActiveOrgs || status.hasPendingMemberInvitations
}

export type MeasurementSystem = 'imperial' | 'metric'

export const DEFAULT_MEASUREMENT_SYSTEM: MeasurementSystem = 'imperial'

export function isMeasurementSystem(value: unknown): value is MeasurementSystem {
  return value === 'imperial' || value === 'metric'
}

export type UserProfile = {
  userId: string
  email: string
  firstName?: string
  lastName?: string
  displayName?: string
  phone?: string
  dateOfBirth?: string
  flywayRegion?: FlywayRegion
  addressLine1?: string
  addressLine2?: string
  city?: string
  state?: string
  zipCode?: string
  country?: string
  emergencyContact?: string
  emergencyPhone?: string
  emergencyRelationship?: string
  emergencyContact2?: string
  emergencyPhone2?: string
  emergencyRelationship2?: string
  vehicleMake?: string
  vehicleModel?: string
  vehicleColor?: string
  vehicleLicensePlate?: string
  vehicleState?: string
  emailVerifiedAt?: string
  phoneVerifiedAt?: string
  isStaff?: boolean
  onboardingStep?: number
  measurementSystem?: MeasurementSystem
  ratingPromptRequestedAt?: string
}

export type UserProfileResult = {
  exists: boolean
  user: UserProfile
}

export type LegalAcceptanceMethod = 'modal' | 'registration' | 'forced_update'

export type LegalAcceptanceResult = {
  success: boolean
  documentId: string
  acceptedAt: string
}

export type AppConfig = {
  trialDurationDays?: number
  trialDurationDaysByOrgType?: { club?: number; outfitter?: number; crew?: number }
  [key: string]: unknown
}
